import { Shareholding } from '@commenda/api';
import { Tooltip } from '@commenda/kakashi';
import { useMemo } from 'react';
import { getStakeholderColorById } from '../constants';

interface SummaryBarProps {
  shareholdings: Shareholding[];
  // May exceed 100% when over-allocated.
  totalOwnershipPercent: number;
  percentScale: number;
  colorIndexById: ReadonlyMap<string, number>;
  hoveredId?: string;
  onHoverChange: (id: string | undefined) => void;
}

export const SummaryBar = ({
  shareholdings,
  totalOwnershipPercent,
  percentScale,
  colorIndexById,
  hoveredId,
  onHoverChange,
}: SummaryBarProps) => {
  const segments = useMemo(
    () =>
      shareholdings
        .map((sh) => ({
          id: sh.stakeholdingId,
          name: sh.name,
          percent:
            (sh.dilutedOwnershipPercent ?? sh.ownershipPercent ?? 0) / percentScale,
          base: getStakeholderColorById(colorIndexById, sh.stakeholdingId).base,
        }))
        .filter((seg) => seg.percent > 0),
    [shareholdings, percentScale, colorIndexById],
  );

  // Over-allocated: divide by the real sum so the bar doesn't overflow.
  const denom = Math.max(100, totalOwnershipPercent / percentScale);

  return (
    <div className="flex flex-col px-xl gap-xs">
      <p className="txt-small-medium text-text-primary">Ownership distribution</p>
      <div className="relative h-6 w-full">
        <div className="absolute inset-x-0 top-1/2 flex h-2 w-full -translate-y-1/2 overflow-hidden rounded-full bg-bg-secondary">
          {segments.map((seg, i) => {
            const isHovered = hoveredId === seg.id;
            const isAnyHovered = hoveredId !== undefined;
            const isLast = i === segments.length - 1;
            return (
              <div
                key={seg.id}
                className={`h-full transition-opacity duration-150 ${
                  isLast ? '' : 'border-r border-white/60'
                }`}
                style={{
                  width: `${(seg.percent / denom) * 100}%`,
                  backgroundColor: seg.base,
                  opacity: isAnyHovered && !isHovered ? 0.2 : 1,
                }}
              />
            );
          })}
        </div>
        <div className="absolute inset-0 flex">
          {segments.map((seg) => (
            <Tooltip
              key={seg.id}
              content={seg.name}
              supportingText={`${seg.percent.toFixed(2)}%`}
              side="top"
              asChild
              className="items-start text-left"
            >
              <div
                onMouseEnter={() => onHoverChange(seg.id)}
                onMouseLeave={() => onHoverChange(undefined)}
                className="h-full cursor-pointer"
                style={{ width: `${(seg.percent / denom) * 100}%` }}
              />
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};
